import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { Sede, Rischio, SocietaSederischio, StatoPolizza } from '../../types';

const STATO_BADGE: Record<StatoPolizza, string> = {
  VERDE:   'bop-badge--success',
  GIALLO:  'bop-badge--warning',
  ARANCIO: 'bop-badge--warning',
  ROSSO:   'bop-badge--danger',
  SCADUTA: 'bop-badge--neutral',
};

export default function AssociazioniRischiPage() {
  const [rischi,        setRischi]        = useState<Rischio[]>([]);
  const [sediAll,       setSediAll]       = useState<Sede[]>([]);
  const [assoc,         setAssoc]         = useState<SocietaSederischio[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [modal,         setModal]         = useState(false);
  const [saving,        setSaving]        = useState(false);

  const {
    societaId: filterSocieta,
    setSocietaId: setFilterSocieta,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Form aggiunta
  const [formSocieta,   setFormSocieta]   = useState('');
  const [formRischio,   setFormRischio]   = useState('');
  const [formSedeIds,   setFormSedeIds]   = useState<number[]>([]);

  const sediPerSocieta = sediAll.filter((s) => !formSocieta || s.societaId === Number(formSocieta));

  const loadAssoc = async (socId?: string) => {
    const qs = socId ? `?societaId=${socId}` : '';
    const d = await apiFetch<{ associazioni: SocietaSederischio[] }>(`/api/assicurazioni/associazioni${qs}`);
    setAssoc(d.associazioni);
  };

  useEffect(() => {
    if (!pronta) return;
    Promise.all([
      apiFetch<{ rischi: Rischio[] }>('/api/assicurazioni/rischi'),
      apiFetch<{ sedi: Sede[] }>('/api/sedi'),
      apiFetch<{ associazioni: SocietaSederischio[] }>(`/api/assicurazioni/associazioni${filterSocieta ? `?societaId=${filterSocieta}` : ''}`),
    ])
      .then(([risRes, sediRes, assocRes]) => {
        setRischi(risRes.rischi);
        setSediAll(sediRes.sedi);
        setAssoc(assocRes.associazioni);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [pronta, filterSocieta]); // eslint-disable-line

  const handleFilterChange = (val: string) => {
    setFilterSocieta(val);
  };

  const openModal = () => {
    setFormSocieta(isUnica && societa.length === 1 ? String(societa[0].id) : '');
    setFormRischio(''); setFormSedeIds([]);
    setError(''); setModal(true);
  };
  const closeModal = () => { setModal(false); setError(''); };

  const toggleSede = (id: number) => {
    setFormSedeIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleFormSocietaChange = (val: string) => {
    setFormSocieta(val);
    setFormSedeIds([]);
  };

  const save = async () => {
    if (!formSocieta || !formRischio) {
      setError('Società e rischio obbligatori'); return;
    }
    setSaving(true);
    try {
      await apiFetch('/api/assicurazioni/associazioni', {
        method: 'POST',
        body: JSON.stringify({ societaId: Number(formSocieta), rischioId: Number(formRischio), sedeIds: formSedeIds }),
      });
      closeModal();
      setLoading(true);
      await loadAssoc(filterSocieta || undefined);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); setLoading(false); }
  };

  const del = async (id: number) => {
    if (!window.confirm('Eliminare questa associazione?')) return;
    try {
      await apiFetch(`/api/assicurazioni/associazioni/${id}`, { method: 'DELETE' });
      setLoading(true);
      await loadAssoc(filterSocieta || undefined);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Associazioni Rischi</h2>
          <p>Rischi assicurabili per società e sede</p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={openModal}>+ Nuova associazione</button>
      </div>

      {error && !modal && <div className="inline-error">{error}</div>}

      {/* Filtro */}
      {!isUnica && (
        <div className="bop-card bop-assoc-rischi__filter-card">
          <div className="bop-assoc-rischi__filter-row">
            <label className="bop-assoc-rischi__filter-label">Società:</label>
            <select className="bop-select bop-assoc-rischi__filter-select"
              value={filterSocieta} onChange={(e) => handleFilterChange(e.target.value)}>
              <option value="">— Tutte —</option>
              {societa.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
        </div>
      )}

      <div className="bop-card">
        <div className="bop-table-wrapper">
          {loading ? <div className="inline-loading">Caricamento…</div> : assoc.length === 0 ? (
            <div className="bop-empty-state">Nessuna associazione trovata.</div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Rischio</th>
                  <th>Società</th>
                  <th>Sede</th>
                  <th>Copertura polizza</th>
                  <th className="bop-assoc-rischi__th-actions"></th>
                </tr>
              </thead>
              <tbody>
                {assoc.map((a) => (
                  <tr key={a.id}>
                    <td><strong>{a.rischio_nome}</strong></td>
                    <td className="bop-text-muted">{a.societa_nome}</td>
                    <td className="bop-text-muted">
                      {a.sedeId ? a.sede_nome : <em className="bop-assoc-rischi__tutta-societa">Tutta la società</em>}
                    </td>
                    <td>
                      {a.polizza_stato ? (
                        <span className={`bop-badge ${STATO_BADGE[a.polizza_stato]}`}>{a.polizza_stato}</span>
                      ) : (
                        <span className="bop-assoc-rischi__non-coperto">Non coperto</span>
                      )}
                    </td>
                    <td>
                      <button className="bop-btn bop-btn-ghost bop-text-danger"
                        onClick={() => del(a.id)}>
                        Elimina
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal && (
        <Modal
          title="Nuova associazione"
          onClose={closeModal}
          footer={
            <><button className="bop-btn bop-btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Salvo…' : 'Aggiungi'}
              </button></>
          }
        >
          {error && <div className="inline-error">{error}</div>}
          {!isUnica && (
            <div className="bop-form-group">
              <label className="bop-label">Società <span className="req">*</span></label>
              <select className="bop-select" value={formSocieta}
                onChange={(e) => handleFormSocietaChange(e.target.value)}>
                <option value="">— Seleziona —</option>
                {societa.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Rischio <span className="req">*</span></label>
            <select className="bop-select" value={formRischio}
              onChange={(e) => setFormRischio(e.target.value)}>
              <option value="">— Seleziona —</option>
              {rischi.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Sedi (lascia vuoto = tutta la società)</label>
            <div className="bop-assoc-rischi__sedi-list">
              {!formSocieta && (
                <span className="bop-assoc-rischi__sedi-placeholder">Seleziona prima una società</span>
              )}
              {formSocieta && sediPerSocieta.length === 0 && (
                <span className="bop-assoc-rischi__sedi-placeholder">Nessuna sede disponibile — verrà associata all'intera società</span>
              )}
              {sediPerSocieta.map((s) => (
                <label key={s.id} className="bop-assoc-rischi__sede-check">
                  <input type="checkbox" checked={formSedeIds.includes(s.id)}
                    onChange={() => toggleSede(s.id)} />
                  {s.nome}{s.citta ? ` (${s.citta})` : ''}
                </label>
              ))}
            </div>
            {formSedeIds.length === 0 && formSocieta && (
              <span className="bop-assoc-rischi__hint">
                Nessuna sede selezionata → associazione a livello di società
              </span>
            )}
          </div>
        </Modal>
      )}
    </Layout>
  );
}
